import { useCallback, useEffect, useRef } from 'react';

/**
 * Minimal preload queue with a concurrency cap. Submitting the same URL twice
 * is a no-op. Failures are silent — the reader's `<img onerror>` path handles
 * user-visible retries.
 *
 * We deliberately do not store the loaded image data; the goal is just to
 * warm the browser HTTP cache so the `<img>` element snaps in instantly when
 * the user navigates to that page. The `Image` constructor will be GC'd as
 * soon as `onload` fires.
 */
export function useImagePreload(concurrency: number = 3): {
  enqueue: (url: string) => void;
  reset: () => void;
} {
  const queueRef = useRef<string[]>([]);
  const inflightRef = useRef<Set<string>>(new Set());
  const seenRef = useRef<Set<string>>(new Set());

  const drain = useCallback(() => {
    while (inflightRef.current.size < concurrency && queueRef.current.length > 0) {
      const url = queueRef.current.shift();
      if (!url) break;
      if (inflightRef.current.has(url)) continue;
      inflightRef.current.add(url);
      const img = new Image();
      const done = () => {
        inflightRef.current.delete(url);
        // Schedule the next drain on a macrotask so we don't recurse
        // synchronously inside the Image event handler.
        setTimeout(drain, 0);
      };
      img.onload = done;
      img.onerror = done;
      img.referrerPolicy = 'no-referrer';
      img.src = url;
    }
  }, [concurrency]);

  const enqueue = useCallback(
    (url: string) => {
      if (!url) return;
      if (seenRef.current.has(url)) return;
      seenRef.current.add(url);
      queueRef.current.push(url);
      drain();
    },
    [drain],
  );

  const reset = useCallback(() => {
    queueRef.current = [];
    inflightRef.current.clear();
    seenRef.current.clear();
  }, []);

  // Clear out on unmount so reader-leave doesn't leak entries to a future
  // mount of the hook.
  useEffect(() => () => reset(), [reset]);

  return { enqueue, reset };
}
